import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { isAxiosError } from "axios";
import { EmailStep } from "./EmailStep";
import { SsoChoiceStep } from "./SsoChoiceStep";
import { LoginForm } from "./LoginForm";
import { type LoginFormInputs, type LoginSteps } from "./types";
import { TwoFactorForm } from "./twoFactorForm";
import { SuccessMessage } from "./successMessage";
import * as analytics from "@/services/analytics";
import * as Sentry from "@sentry/react";
import { useNavigate, useSearchParams } from "react-router";
import { authenticateUser, checkSsoDomain, resend2FACode } from "@/views/auth/api";
import { warmMainweb } from "@/helpers/warmup";
import { toast } from "react-toastify";
import { useAppDispatch } from "@/store/hooks";
import { authenticationApi } from "@/api/authentication";
import { getLoginRedirectPath } from "./redirectState";

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_user_not_found: "Your account was not found. Contact your administrator.",
  sso_not_enabled: "SSO is not enabled for your organization.",
  sso_account_inactive: "Your account has been deactivated. Contact your administrator.",
};

const redirectToSso = (email: string, redirectAfter: string) => {
  const params = new URLSearchParams({ email, redirect_after: redirectAfter });
  window.location.href = `/pf-auth/sso/login?${params.toString()}`;
};

export const AuthenticationView: React.FC = () => {
  const methods = useForm<LoginFormInputs>({
    defaultValues: {
      email: "",
      password: "",
      code: "",
      channel: "sms",
    },
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState<LoginSteps>("email");
  const [ssoOrgName, setSsoOrgName] = React.useState("");
  const [emailForLogin, setEmailForLogin] = React.useState("");
  const [isCheckingDomain, setIsCheckingDomain] = React.useState(false);
  const [searchParams] = useSearchParams();
  const state = searchParams.get("state");
  const ssoError = searchParams.get("error");
  const emailFromQuery = searchParams.get("email");
  const autoSubmittedRef = React.useRef(false);

  React.useEffect(() => {
    if (ssoError && SSO_ERROR_MESSAGES[ssoError]) {
      setError(SSO_ERROR_MESSAGES[ssoError]);
    }
  }, [ssoError]);

  const redirectAfter = getLoginRedirectPath(state);

  const refreshAuthenticatedUser = React.useCallback(async () => {
    const request = dispatch(
      authenticationApi.endpoints.getAuthenticatedUser.initiate(undefined, {
        forceRefetch: true,
      }),
    );

    try {
      await request.unwrap();
    } finally {
      request.unsubscribe();
    }
  }, [dispatch]);

  const handleEmailSubmit = async (email: string) => {
    setError(null);
    setIsCheckingDomain(true);

    try {
      const result = await checkSsoDomain(email);
      setEmailForLogin(email);

      warmMainweb();

      if (!result.sso_enabled) {
        methods.setValue("email", email);
        setStep("creds");
      } else if (result.sso_mode === "sso") {
        redirectToSso(email, redirectAfter);
      } else if (result.sso_mode === "both") {
        setSsoOrgName(result.organization_name || "Your organization");
        setStep("sso_choice");
      }
    } catch (err) {
      if (isAxiosError(err)) {
        setError((err.response?.data as { detail?: string })?.detail || "Failed to check email domain.");
      } else {
        Sentry.captureException(err);
        setError("Failed to check email domain. Please try again.");
      }
    } finally {
      setIsCheckingDomain(false);
    }
  };

  React.useEffect(() => {
    if (emailFromQuery && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      void handleEmailSubmit(emailFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromQuery]);

  const handleSsoLogin = () => {
    analytics.loginSso();
    redirectToSso(emailForLogin, redirectAfter);
  };

  const handlePasswordLogin = () => {
    methods.setValue("email", emailForLogin);
    setStep("creds");
  };

  const handleBackToEmail = () => {
    setError(null);
    setStep("email");
  };

  const onSubmit = async (payload: LoginFormInputs) => {
    setError(null);

    try {
      const result = await authenticateUser(payload);

      if (result.status === "2fa_required") {
        methods.setValue("channel", result.channel ?? payload.channel);
        if (result.user_email) {
          analytics.twoFactorAuthentication(result.user_email);
        }
        setStep("2fa");
      } else if (result.status === "success") {
        await refreshAuthenticatedUser();
        analytics.identify(result.user_email || payload.email);
        analytics.loginStandard();
        setStep("success");
        navigate(redirectAfter);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        setError((error.response?.data as { detail?: string })?.detail || "Failed to sign in. Please try again.");
      } else {
        Sentry.captureException(error);
        setError("Failed to sign in. Please try again.");
      }
    }
  };

  const handleResendCode = async (channel?: "email" | "sms") => {
    setError(null);
    const values = methods.getValues();

    try {
      await resend2FACode({
        email: values.email,
        channel: channel || values.channel,
      });
      toast.success(
        `Code sent. A new verification code has been sent to your ${(channel || values.channel) === "sms" ? "phone" : "email"}.`,
      );
      if (channel && channel !== values.channel) {
        methods.setValue("channel", channel);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        setError((error.response?.data as { detail?: string })?.detail || "Failed to resend code. Please try again.");
      } else {
        Sentry.captureException(error);
        setError("Failed to resend code. Please try again.");
      }
    }
  };

  if (step === "email" || step === "sso_choice") {
    return (
      <>
        {step === "email" && (
          <EmailStep
            error={error}
            onSubmit={handleEmailSubmit}
            isLoading={isCheckingDomain}
            initialEmail={emailFromQuery ?? ""}
          />
        )}
        {step === "sso_choice" && (
          <SsoChoiceStep
            organizationName={ssoOrgName}
            onSsoLogin={handleSsoLogin}
            onPasswordLogin={handlePasswordLogin}
          />
        )}
      </>
    );
  }

  return (
    <form className="space-y-6" onSubmit={methods.handleSubmit(onSubmit)} method="post">
      <FormProvider {...methods}>
        {step === "creds" && (
          <LoginForm error={error} isLoading={methods.formState.isSubmitting} onBack={handleBackToEmail} />
        )}
        {step === "2fa" && (
          <TwoFactorForm resendCode={handleResendCode} error={error} isLoading={methods.formState.isSubmitting} />
        )}
        {step === "success" && <SuccessMessage />}
      </FormProvider>
    </form>
  );
};
